// index.js
import React, { useEffect, useRef, useState, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { UserProvider } from "../contexts/UserContext";
import UserContext from "../contexts/UserContext";
import { NotificationsProvider } from "../contexts/NotificationsContext";
import { WhitelabelProvider } from "../contexts/WhitelabelContext";
import { isAdvancedPlan, isPersonalPlan } from "../utils/PlanUtils";
import { FEATURE_SLUGS } from "../constants/FeatureSlugs";

import "./index.css";
import "./chart.css";
import "./step.css";

import Footer from "./layouts/Footer";
import Header from "./layouts/nav/Header";

import Login from "../screens/Login";
import PrivacyPolicy from "../screens/PrivacyPolicy";
import TermsOfUse from "../screens/TermsOfUse";
import GeneralCustomers from "../screens/GeneralCustomers";
import CustomerDetails from "../screens/CustomerDetails";
import Users from "../screens/Users";
import UserDetails from "../screens/UserDetails";
// import Register from "../screens/Register";
// import Checkout from "../screens/Checkout";
import Signup from "../screens/Signup";
import PublicPlayground from "./components/PublicPlayground";
import WhatsAppConnect from "../screens/WhatsAppPublicConnect";
import Campaigns from "../screens/Campaigns";
import CampaignDetails from "../screens/CampaignDetails";
import WhatsappTemplates from "../screens/WhatsappTemplates";
import Templates from "../screens/Templates";
import WhatsAppTemplateForm from "./components/Templates/WhatsAppTemplateForm";
import EmailTemplateForm from "./components/Templates/EmailTemplateForm";
import TemplateTypeSelector from "./components/Templates/TemplateTypeSelector";
import WhatsappAgents from "../screens/WhatsappAgents";
import Account from "../jsx/components/WhatsAppChat/Account";
import WhatsAppChat from "../jsx/components/WhatsAppChat/WhatsAppChat";
import { parseCookies } from "nookies";
import Calendar from "./components/FullCalendar";
import AutomationsList from "../screens/Automations/AutomationsList";
import AutomationDetail from "../screens/Automations/AutomationDetail";
import AutomationRuleForm from "../screens/Automations/AutomationRuleForm";
import AutomationForm from "../screens/Automations/AutomationForm";
import AutomationWithRulesWizard from "../screens/Automations/AutomationWithRulesWizard";
import MobileHeader from "./layouts/nav/MobileHeader";
import ContactBoard from "../jsx/components/ContactsBoard/";
import BusinessSettings from "../screens/Business/BusinessSettings";
import AgentWizard from "./components/Agents/AgentWizard";
import CustomerList from "../screens/Customers/CustomerList";
import TagsManager from "../screens/Tags/TagsManager";
import CreateOrganization from "../screens/Organization/modals/CreateOrganization";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import UsersPage from "../screens/Users/UsersPage";
import WelcomeScreen from "../screens/Welcome/WelcomeScreen";
import SmartDashboardRoute from "./components/Auth/SmartDashboardRoute";
import OrganizationOverview from "../screens/Organization/OrganizationOverview";
import OrganizationCustomization from "../screens/Organization/OrganizationCustomization";
import OrganizationSubaccountsList from "../screens/Organization/OrganizationSubaccountsList";
import AccountActivation from "../screens/AccountActivation";
import { AgentDeploymentsList } from "../screens/AgentDeployments";
import { CustomerBillingProfilesList } from "../screens/CustomerBillingProfiles";
import { UserSubscriptionsList } from "../screens/UserSubscriptions";
import MyPlanScreen from "../screens/MyPlan";
import KnowledgeBase from "./components/KnowledgeBase";
import ImageLibrary from "./components/ImageLibrary";
import Playground from "./components/Playground";
import TrainingDashboard from "./components/Training/TrainingDashboard";
import CategoryView from "./components/Training/CategoryView";
import VideoPlayer from "./components/Training/VideoPlayer";
import TrainingManagement from "./components/Training/TrainingManagement";
import CoursesDashboard from "./components/Courses/CoursesDashboard";
import AgencyDashboard from "./components/Dashboard/AgencyDashboard";
import AgencyWelcome from "../screens/AgencyWelcome";
import Support from "./components/Support";
import Channels from "../screens/Channels";
import Integrations from "../screens/Integrations";
import { PublicationsList } from "../screens/Publications";
import GlobalSettings from "../screens/Settings/GlobalSettings";
import { FEATURE_IDS } from "../constants/FeatureIds";
import WebhookTemplateForm from "./components/Templates/WebhookTemplateForm";
import Dashboard from "./components/Dashboard/Dashboard";
import Insights from "../screens/Insights";
import Gamification from "../screens/Gamification";
import AdminUserList from "../screens/Admin/AdminUserList";
import MarketplacePublications from "../screens/Marketplace/MarketplacePublications";
import MarketplacePublicationForm from "../screens/Marketplace/MarketplacePublicationForm";
import MarketplacePublicationDetails from "../screens/Marketplace/MarketplacePublicationDetails";
import MarketplaceShop from "../screens/Marketplace/MarketplaceShop";
import ShopList from "../screens/Marketplace/ShopList";
import MarketplaceLeads from "../screens/Marketplace/MarketplaceLeads";
import SellerProfile from "../screens/Marketplace/SellerProfile";
import AgentCreator from "./components/Agents/AgentCreator";
import MarketplaceBanners from "../screens/Marketplace/MarketplaceBanners";
import MarketplaceBannerForm from "../screens/Marketplace/MarketplaceBannerForm";
import ProductsList from "../screens/Products";
import AgentHub from "../screens/AgentHub";
import { AgentsFlowScreen, AgentsFlowCanvas, AgentsFlowAutomations } from "../screens/AgentsFlow";
import GoogleOAuthCallback from "../screens/AgentsFlow/components/GoogleOAuthCallback";
import { useParams } from "react-router-dom";
import ScheduledFollowupsList from "../screens/Automations/ScheduledFollowupsList";
import { RestrictedAccessBanner, RestrictedAccessScreen } from "./components/RestrictedAccess";
import ServiceCenter from "../screens/ServiceCenter";
import { OnboardingFlow, OnboardingService } from "../screens/Onboarding";

// Wrapper para ProductsList com agentId da URL
const AgentProductsWrapper = () => {
  const { agentId } = useParams();

  // Simular agentContext básico baseado no agentId
  const agentContext = {
    id: parseInt(agentId),
    name: `Agente ${agentId}`, // Isso seria idealmente carregado de uma API
  };

  return (
    <ProductsList
      hubMode={true}
      agentId={parseInt(agentId)}
      agentContext={agentContext}
    />
  );
};

// Wrapper para Templates com agentId da URL
const AgentTemplatesWrapper = () => {
  const { agentId } = useParams();

  // Simular agentContext básico baseado no agentId
  const agentContext = {
    id: parseInt(agentId),
    name: `Agente ${agentId}`, // Isso seria idealmente carregado de uma API
  };

  return (
    <Templates
      hubMode={true}
      agentId={parseInt(agentId)}
      agentContext={agentContext}
    />
  );
};

// Componente de bloqueio por plano
const PlanGuard = ({
  allowedPlans,
  children,
  blockMessage = "Recurso não disponível no seu plano atual",
  requiredFeature = null, // Novo: Feature ID para verificação em subaccounts
}) => {
  const { getUserPlan, isSubaccount, hasPermission, permissionsLoading, user } =
    useContext(UserContext);

  const currentPlan = getUserPlan();

  // Se é subaccount, verificar permissões por feature em vez de plano
  if (isSubaccount()) {
    // Se feature foi especificada, verificar se tem acesso através de permissões
    if (requiredFeature) {
      // Admin sempre tem acesso
      if (user?.superuser_level === 1) {
        return children;
      }

      // Se ainda está carregando permissões, aguardar
      if (permissionsLoading) {
        return (
          <div className="plan-restriction-page">
            <div className="plan-restriction-content">
              <div className="plan-restriction-icon">⏳</div>
              <h3>Verificando Permissões</h3>
              <p>Aguarde enquanto verificamos suas permissões...</p>
            </div>
          </div>
        );
      }

      // Verificar se tem a feature e permissão de leitura
      if (!hasPermission(requiredFeature, "read")) {
        return (
          <div className="plan-restriction-page">
            <div className="plan-restriction-content">
              <div className="plan-restriction-icon">🔒</div>
              <h3>Acesso Negado</h3>
              <p>Você não tem permissão para acessar este módulo.</p>
              <p>
                <strong>💡 O que fazer:</strong> Entre em contato com o
                administrador da sua conta para solicitar acesso a esta
                funcionalidade.
              </p>
            </div>
          </div>
        );
      }
    } else {
      // Se não foi especificada feature, permitir acesso para subaccounts
      // (compatibilidade com código legado)
      return children;
    }
  } else {
    // Para contas normais, usar verificação por plano como antes
    if (allowedPlans && !allowedPlans.includes(currentPlan)) {
      return (
        <div className="plan-restriction-page">
          <div className="plan-restriction-content">
            <div className="plan-restriction-icon">🔒</div>
            <h3>Recurso Indisponível</h3>
            <p>{blockMessage}</p>
            <p>
              Seu plano atual:{" "}
              <strong>{currentPlan === 3 ? "Business" : "Pro"}</strong>
            </p>
            <p>
              Para acessar este recurso, considere fazer upgrade para o plano
              Pro.
            </p>
          </div>
        </div>
      );
    }
  }

  return children;
};

// Componente interno para gerenciar o layout baseado na rota
const AppLayout = ({ isLoading, logged, setIsLoading }) => {
  const location = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useState(
    window.innerWidth <= 1024 ? false : true
  );
  const {
    getUserPlan,
    isRestrictedAccess,
    getRestrictionReason,
    isRouteAllowedForRestricted,
    RESTRICTED_ALLOWED_ROUTES,
    user,
    isSubaccount,
    accountContextLoaded,
    isInitializing,
  } = useContext(UserContext);

  // ========== ONBOARDING STATE ==========
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingCheckedRef = useRef(false);

  useEffect(() => {
    // Ja verificou — nao verificar novamente
    if (onboardingCheckedRef.current) return;
    // Aguardar inicializacao completa
    if (!logged || !user || isInitializing) return;
    // Aguardar /auth/me/ resolver para ter accountContext definitivo
    // Sem isso, isSubaccount() retorna false antes do state ser preenchido
    if (!accountContextLoaded) return;

    // Marcar como verificado ANTES da chamada async (evita re-execucao)
    onboardingCheckedRef.current = true;

    // Nao mostrar para subaccounts ou staff
    if (isSubaccount()) return;
    if (user.is_staff) return;

    const checkOnboarding = async () => {
      try {
        const status = await OnboardingService.getStatus();
        if (!status.completed && !status.skipped) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.warn("Could not check onboarding status:", error);
      }
    };

    checkOnboarding();
  }, [logged, user, isInitializing, accountContextLoaded]);

  // Rotas que não devem mostrar o header
  const noHeaderRoutes = ["/login", "/register", "/activate", "/checkout", "/signup", "/politica-de-privacidade", "/termos-de-uso"];
  const shouldShowHeader =
    logged &&
    !noHeaderRoutes.includes(location.pathname) &&
    !location.pathname.startsWith("/testar") &&
    !location.pathname.startsWith("/conectar");

  // Rotas que não devem ter o main-container (login, register, activate, checkout, testar, conectar)
  const noContainerRoutes = ["/login", "/register", "/activate", "/checkout", "/signup", "/politica-de-privacidade", "/termos-de-uso"];
  const shouldUseContainer =
    !noContainerRoutes.includes(location.pathname) &&
    !location.pathname.startsWith("/testar") &&
    !location.pathname.startsWith("/conectar");
  const chatPaths = ["/service-center", "/playground", "/service-center", ];
  const path = location.pathname;

  // Verifica se é uma rota de chat fullscreen (inclui paths exatos e paths que começam com certos prefixos)
  const isChatFullscreen = chatPaths.includes(path) || path.startsWith("/agents-flow/canvas");

  // ========== VERIFICACAO DE ACESSO RESTRITO ==========
  // Verifica se o usuario tem acesso restrito por plano irregular
  const isRestrictedUser = logged && isRestrictedAccess && isRestrictedAccess();
  const currentPath = location.pathname;
  const isAllowedRoute = isRouteAllowedForRestricted
    ? isRouteAllowedForRestricted(currentPath)
    : false;

  // Se usuario restrito e em rota nao permitida, renderizar tela de bloqueio
  if (isRestrictedUser && !isAllowedRoute && shouldUseContainer) {
    const restrictionReason = getRestrictionReason ? getRestrictionReason() : null;

    return (
      <>
        {/* Header - apenas se logado e nao for login/register */}
        {shouldShowHeader &&
          (window.innerWidth <= 1024 ? (
            <MobileHeader
              sidebarExpanded={sidebarExpanded}
              setSidebarExpanded={setSidebarExpanded}
            />
          ) : (
            <Header
              sidebarExpanded={sidebarExpanded}
              setSidebarExpanded={setSidebarExpanded}
            />
          ))}

        <div
          id="main-container"
          className="main-container sidebar-expanded"
        >
          <RestrictedAccessScreen
            reason={restrictionReason}
            allowedRoutes={RESTRICTED_ALLOWED_ROUTES || ['/', '/billing', '/subscriptions', '/user-details', '/my-plan']}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header - apenas se logado e não for login/register */}
      {shouldShowHeader &&
        (window.innerWidth <= 1024 ? (
          <MobileHeader
            sidebarExpanded={sidebarExpanded}
            setSidebarExpanded={setSidebarExpanded}
          />
        ) : (
          <Header
            sidebarExpanded={sidebarExpanded}
            setSidebarExpanded={setSidebarExpanded}
          />
        ))}

      {/* Onboarding para novos usuarios */}
      {showOnboarding && shouldShowHeader && (
        <OnboardingFlow
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      {/* Conteúdo Principal */}
      {isLoading ? (
        <div className="loading-overlay">
          <MoonLoader color="var(--primary)" size={40} />
        </div>
      ) : shouldUseContainer ? (
        <div
          id="main-container"
          className={`main-container sidebar-expanded ${
            isChatFullscreen ? "chat-fullscreen" : ""
          }`}
        >
          {/* Banner de acesso restrito para rotas permitidas */}
          {isRestrictedUser && isAllowedRoute && <RestrictedAccessBanner />}

          <Routes>
            {/* ========== DASHBOARD (ROTA BÁSICA) ========== */}
            {isAdvancedPlan(getUserPlan()) ? (
              <Route
                path="/"
                element={
                  <ProtectedRoute
                    requiredPermission={() => isAdvancedPlan(getUserPlan())}
                  >
                    <AgencyWelcome />
                  </ProtectedRoute>
                }
              />
            ) : (
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <SmartDashboardRoute />
                  </ProtectedRoute>
                }
              />
            )}

            <Route
              path="/metrics"
              element={
                <ProtectedRoute
                  requiredPermission={() => isAdvancedPlan(getUserPlan())}
                >
                  {/*  <PlanGuard
                    allowedPlans={[2, 4, 5, 6]}
                    requiredFeature={FEATURE_IDS.METRICAS}
                    blockMessage="As métricas avançadas estão disponíveis apenas no plano Pro."
                  >
                    
                  </PlanGuard> */}
                  <AgencyDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/business-metrics"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    user.superuser_level === 1 || user.superuser_level === 2
                  }
                >
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute
                  requiredFeatureSlug={FEATURE_SLUGS.RELATORIOS_AVANCADOS}
                  requiredAction="read"
                >
                  <Insights />
                </ProtectedRoute>
              }
            />

            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <WelcomeScreen />
                </ProtectedRoute>
              }
            />

            {/* ========== CHAT E COMUNICAÇÃO ========== */}
            <Route
              path="/service-center"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.CHAT}
                  requiredAction="read"
                >
                  <ServiceCenter />
                </ProtectedRoute>
              }
            />

            {/* ========== CENTRAL DE ATENDIMENTO ========== */}
            <Route
              path="/service-center"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.CHAT}
                  requiredAction="read"
                >
                  <ServiceCenter />
                </ProtectedRoute>
              }
            />

            {/* ========== AGENDA ========== */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AGENDA}
                  requiredAction="read"
                >
                  <Calendar />
                </ProtectedRoute>
              }
            />

            {/* ========== AGENTES IA ========== */}
            <Route path="/agents" element={<WhatsappAgents />} />
            <Route path="/agents/create" element={<AgentCreator />} />
            <Route
              path="/agents/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.EDITAR_AGENTE}
                  requiredAction="update"
                >
                  <AgentWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents/:agentId/hub"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MEUS_AGENTES}
                  requiredAction="read"
                >
                  <AgentHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents/:agentId/products"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MEUS_AGENTES}
                  requiredAction="read"
                >
                  <AgentProductsWrapper />
                </ProtectedRoute>
              }
            />

            {/* ========== AGENTS FLOW (NOVO CRIADOR VISUAL) ========== */}
            <Route
              path="/agents-flow"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MEUS_AGENTES}
                  requiredAction="read"
                >
                  <AgentsFlowScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents-flow/canvas"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MEUS_AGENTES}
                  requiredAction="create"
                >
                  <AgentsFlowCanvas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents-flow/canvas/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.EDITAR_AGENTE}
                  requiredAction="update"
                >
                  <AgentsFlowCanvas />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents-flow/automations/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.EDITAR_AGENTE}
                  requiredAction="read"
                >
                  <AgentsFlowAutomations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agents-flow/:agentId/oauth/callback"
              element={<GoogleOAuthCallback />}
            />

            {/* ========== CLIENTES ========== */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.BASE_CLIENTES}
                  requiredAction="read"
                >
                  <CustomerList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/general-customers"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.BASE_CLIENTES}
                  requiredAction="read"
                >
                  <GeneralCustomers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-details/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.BASE_CLIENTES}
                  requiredAction="read"
                >
                  <CustomerDetails />
                </ProtectedRoute>
              }
            />

            {/* ========== TEMPLATES ========== */}
            <Route
              path="/templates"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="read"
                >
                  <PlanGuard
                    allowedPlans={[2, 3, 4, 5, 6]}
                    requiredFeature={FEATURE_IDS.TEMPLATES}
                    blockMessage="Os templates avançados estão disponíveis apenas no plano Pro."
                  >
                    <Templates />
                  </PlanGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/new"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <TemplateTypeSelector />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/whatsapp/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <WhatsAppTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/whatsapp/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <WhatsAppTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/email/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <EmailTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/email/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <EmailTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/webhook/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <WebhookTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/webhook/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <WebhookTemplateForm />
                </ProtectedRoute>
              }
            />
            {/* Rota de compatibilidade */}
            <Route
              path="/whatsapp-templates"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="read"
                >
                  <WhatsappTemplates />
                </ProtectedRoute>
              }
            />

            {/* ========== CAMPANHAS/PUBLICAÇÕES ========== */}
            {/*  <Route
              path="/campaigns"
              element={
                <ProtectedRoute
                  requiredModule="Publicações"
                  requiredAction="read"
                >
                  <Campaigns />
                </ProtectedRoute>
              }
            /> */}
            {/*  <Route
              path="/detalhes-campanha"
              element={
                <ProtectedRoute
                  requiredModule="Publicações"
                  requiredAction="read"
                >
                  <CampaignDetails />
                </ProtectedRoute>
              }
            /> */}
            {/* <Route
              path="/detalhes-campanha/:id"
              element={
                <ProtectedRoute
                  requiredModule="Publicações"
                  requiredAction="read"
                >
                  <CampaignDetails />
                </ProtectedRoute>
              }
            /> */}

            {/* ========== AUTOMAÇÕES ========== */}
            {/*  <Route
              path="/automations"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="read"
                >
                  <PlanGuard
                    allowedPlans={[2, 3, 4, 5, 6]}
                    requiredFeature={FEATURE_IDS.AUTOMACOES}
                    blockMessage="As automações estão disponíveis apenas no plano Pro."
                  >
                    <AutomationsList />
                  </PlanGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/automations/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="create"
                >
                  <AutomationWithRulesWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automations/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="update"
                >
                  <AutomationRuleForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automations/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="read"
                >
                  <AutomationDetail />
                </ProtectedRoute>
              }
            /> */}

            {/* ========== AUTOMAÇÕES DO AGENTHUB ========== */}
            <Route
              path="/agent-hub/:agentId/automations"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="read"
                >
                  <PlanGuard
                    allowedPlans={[2, 3, 4, 5, 6]}
                    requiredFeature={FEATURE_IDS.AUTOMACOES}
                    blockMessage="As automações estão disponíveis apenas no plano Pro."
                  >
                    <AutomationsList />
                  </PlanGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/automations/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="create"
                >
                  <AutomationWithRulesWizard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/agent-hub/:agentId/automations/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="update"
                >
                  <AutomationRuleForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/automations/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="read"
                >
                  <AutomationDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/agent-hub/:agentId/scheduled-followups"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.AUTOMACOES}
                  requiredAction="read"
                >
                  <ScheduledFollowupsList />
                </ProtectedRoute>
              }
            />

            {/* ========== TEMPLATES DO AGENTHUB ========== */}
            <Route
              path="/agent-hub/:agentId/templates"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="read"
                >
                  <PlanGuard
                    allowedPlans={[2, 3, 4, 5, 6]}
                    requiredFeature={FEATURE_IDS.TEMPLATES}
                    blockMessage="Os templates avançados estão disponíveis apenas no plano Pro."
                  >
                    <AgentTemplatesWrapper />
                  </PlanGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/new"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <TemplateTypeSelector />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/whatsapp/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <WhatsAppTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/whatsapp/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <WhatsAppTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/email/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <EmailTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/email/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <EmailTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/webhook/create"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="create"
                >
                  <WebhookTemplateForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agent-hub/:agentId/templates/webhook/edit/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TEMPLATES}
                  requiredAction="update"
                >
                  <WebhookTemplateForm />
                </ProtectedRoute>
              }
            />

            {/* ========== CRM ========== */}
            <Route
              path="/crm"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.CRM_PIPELINE}
                  requiredAction="read"
                >
                  <ContactBoard />
                </ProtectedRoute>
              }
            />

            {/* ========== TAGS & CATEGORIAS ========== */}
            <Route
              path="/tags"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TAGS_CATEGORIAS}
                  requiredAction="read"
                >
                  <TagsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags/:id"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.TAGS_CATEGORIAS}
                  requiredAction="update"
                >
                  <TagsManager />
                </ProtectedRoute>
              }
            />

            {/* ========== USUÁRIOS (ROTAS CRÍTICAS) ========== */}
            <Route
              path="/users"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.USUARIOS}
                  requiredAction="read"
                  requireSuperuser={true}
                  requiredLevel={3}
                  showLoadingForPermissions={true}
                  checkUsage={true}
                >
                  <UsersPage />
                </ProtectedRoute>
              }
            />

            {/* ========== PERFIL DO USUÁRIO ========== */}
            <Route
              path="/user-details"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MEU_PERFIL}
                  requiredAction="read"
                >
                  <UserDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-details/:id"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    user.can_create_users || user.superuser_level <= 2
                  }
                >
                  <UserDetails />
                </ProtectedRoute>
              }
            />

            {/* ========== CONFIGURAÇÕES EMPRESARIAIS ========== */}
            <Route
              path="/business-settings"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.MINHA_EMPRESA}
                  requiredAction="update"
                  requiredLevel={2}
                  showLoadingForPermissions={true}
                >
                  <BusinessSettings />
                </ProtectedRoute>
              }
            />

            {/* ========== ORGANIZAÇÃO ========== */}
            <Route
              path="/organization-clients"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    isAdvancedPlan(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <OrganizationSubaccountsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/organization"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    isAdvancedPlan(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <OrganizationOverview />
                </ProtectedRoute>
              }
            />

            <Route
              path="/organization/customization"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    isAdvancedPlan(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <OrganizationCustomization />
                </ProtectedRoute>
              }
            />

            <Route
              path="/agents-implementation"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    isAdvancedPlan(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <AgentDeploymentsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/billing"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    (isAdvancedPlan(getUserPlan()) || isPersonalPlan(getUserPlan())) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <CustomerBillingProfilesList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    (isAdvancedPlan(getUserPlan()) || isPersonalPlan(getUserPlan())) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <UserSubscriptionsList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-plan"
              element={
                <ProtectedRoute
                  featureId={FEATURE_IDS.FINANCIAL_BILLING}
                  fallbackMessage="Acesso ao gerenciamento de planos requer permissão."
                >
                  <MyPlanScreen />
                </ProtectedRoute>
              }
            />

            <Route
              path="/publications"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    isAdvancedPlan(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <PublicationsList />
                </ProtectedRoute>
              }
            />

            {/* ========== MARKETPLACE (NOVAS ROTAS) ========== */}
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplacePublications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/create"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplacePublicationForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/edit/:id"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplacePublicationForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/:id"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplacePublicationDetails />
                </ProtectedRoute>
              }
            />
            {/* ========== MARKETPLACE LEADS ========== */}
            <Route
              path="/marketplace/leads"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplaceLeads />
                </ProtectedRoute>
              }
            />
            {/* ========== MARKETPLACE SELLER PROFILE ========== */}
            <Route
              path="/marketplace/seller-profile"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    getUserPlan() > 1 &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <SellerProfile />
                </ProtectedRoute>
              }
            />
            {/* ========== MARKETPLACE SHOPS ========== */}
            <Route
              path="/marketplace/shop"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    [2, 5, 9, 10, 11].includes(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <ShopList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/shop/create"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    [2, 5, 9, 10, 11].includes(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplaceShop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace/shop/edit/:id"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    [2, 5, 9, 10, 11].includes(getUserPlan()) &&
                    (user.superuser_level === 1 || user.superuser_level === 2)
                  }
                >
                  <MarketplaceShop />
                </ProtectedRoute>
              }
            />

            {/* ========== PRODUTOS (MÓDULO PRODUCTS) ========== */}
            <Route
              path="/products"
              element={
                <ProtectedRoute
                  requiredPermission={(user) => user.superuser_level <= 3}
                >
                  <ProductsList />
                </ProtectedRoute>
              }
            />

            {/* ========== PLAYGROUND (NOVO MÓDULO) ========== */}
            {/* <Route
              path="/playground"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.PLAYGROUND}
                  requiredAction="read"
                >
                  <Playground />
                </ProtectedRoute>
              }
            /> */}

            {/* ========== TREINAMENTOS ========== */}
            {getUserPlan() !== 4 && (
              <Route
                path="/training"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.TREINAMENTOS}
                    requiredAction="read"
                  >
                    <TrainingDashboard />
                  </ProtectedRoute>
                }
              />
            )}
            {getUserPlan() !== 4 && (
              <Route
                path="/training/category/:categoryId"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.TREINAMENTOS}
                    requiredAction="read"
                  >
                    <CategoryView />
                  </ProtectedRoute>
                }
              />
            )}
            {getUserPlan() !== 4 && (
              <Route
                path="/training/video/:videoId"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.TREINAMENTOS}
                    requiredAction="read"
                  >
                    <VideoPlayer />
                  </ProtectedRoute>
                }
              />
            )}
            {getUserPlan() !== 4 && (
              <Route
                path="/training/manage"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.GERENCIAR_TREINAMENTOS}
                    requiredAction="create"
                    requiredLevel={2}
                  >
                    <TrainingManagement />
                  </ProtectedRoute>
                }
              />
            )}

            {/* ========== CURSOS MERCADO AGENTES ========== */}
            <Route
              path="/courses"
              element={
                <ProtectedRoute
                // requiredPermission={(user) =>
                //   user && [2, 3, 4, 5, 7, 8, 9, 10, 11].includes(user.plan_id) ##VOLTAAAAAAAAAAAAAAAAAAAAAAAR AQUIII
                // }
                >
                  <CoursesDashboard />
                </ProtectedRoute>
              }
            />

            {/* ========== ESTATÍSTICAS (DESCONTINUADO) ========== */}
            <Route
              path="/statistics"
              element={
                <ProtectedRoute requiredLevel={1}>
                  <div className="container mt-5">
                    <div className="alert alert-warning">
                      <h4>Módulo Descontinuado</h4>
                      <p>
                        O módulo de Estatísticas foi descontinuado e substituído
                        pelas métricas avançadas.
                      </p>
                      <a href="/metrics" className="btn btn-primary">
                        Ir para Métricas
                      </a>
                    </div>
                  </div>
                </ProtectedRoute>
              }
            />

            {/* ========== CANAIS ========== */}
            <Route
              path="/channels"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.CANAIS}
                  requiredAction="read"
                  requiredLevel={2}
                >
                  <Channels />
                </ProtectedRoute>
              }
            />

            {/* ========== INTEGRAÇÕES ========== */}
            <Route path="/integrations" element={<Integrations />} />

            {/* ========== PERSONALIZAÇÕES ========== */}
            <Route
              path="/customizations"
              element={
                <ProtectedRoute requiredLevel={2}>
                  <div className="container mt-5">
                    <h2>Personalizações</h2>
                    <p>Configurações visuais e comportamentais</p>
                  </div>
                </ProtectedRoute>
              }
            />

            {/* ========== SUPORTE ========== */}
            {getUserPlan() !== 4 && (
              <Route
                path="/support"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.SUPORTE}
                    requiredAction="read"
                  >
                    <Support />
                  </ProtectedRoute>
                }
              />
            )}

            {/* ========== GAMIFICAÇÃO ========== */}
            <Route
              path="/gamification"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    [2, 3, 4, 5, 7, 8, 9, 10, 11].includes(user.plan_id)
                  }
                  requiredAction="read"
                >
                  <Gamification />
                </ProtectedRoute>
              }
            />

            {/* ========== CONFIGURAÇÕES GLOBAIS ========== */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.CONFIGURACOES_GLOBAIS}
                  requiredAction="read"
                  requiredLevel={2}
                >
                  <GlobalSettings />
                </ProtectedRoute>
              }
            />

            {/* ========== ADMINISTRAÇÃO GLOBAL (is_staff = true) ========== */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.ADMIN_USUARIOS}
                  requiredAction="read"
                  requiredPermission={(user) => user.is_staff}
                >
                  <AdminUserList />
                </ProtectedRoute>
              }
            />

            {getUserPlan() !== 4 && (
              <Route
                path="/admin/training/manage"
                element={
                  <ProtectedRoute
                    requiredFeature={FEATURE_IDS.ADMIN_TREINAMENTOS}
                    requiredAction="read"
                    requiredPermission={(user) => user.is_staff}
                  >
                    <TrainingManagement />
                  </ProtectedRoute>
                }
              />
            )}

            <Route
              path="/admin/courses"
              element={
                <ProtectedRoute
                  requiredFeature={FEATURE_IDS.ADMIN_CURSOS}
                  requiredAction="read"
                  requiredPermission={(user) => user.is_staff}
                >
                  <CoursesDashboard />
                </ProtectedRoute>
              }
            />

            {/* ========== MARKETPLACE BANNERS (ADMIN ONLY) ========== */}
            <Route
              path="/admin/marketplace/banners"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    user.is_staff && user.superuser_level === 1
                  }
                >
                  <MarketplaceBanners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/marketplace/banners/create"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    user.is_staff && user.superuser_level === 1
                  }
                >
                  <MarketplaceBannerForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/marketplace/banners/edit/:id"
              element={
                <ProtectedRoute
                  requiredPermission={(user) =>
                    user.is_staff && user.superuser_level === 1
                  }
                >
                  <MarketplaceBannerForm />
                </ProtectedRoute>
              }
            />

            {/* ========== PÁGINA 404 ========== */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <div className="container mt-5">
                    <div className="text-center">
                      <h2>Página não encontrada</h2>
                      <p>A página que você está procurando não existe.</p>
                      <a href="/" className="btn btn-primary">
                        Voltar ao Dashboard
                      </a>
                    </div>
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      ) : (
        // Rotas sem container (login, register, activate)
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* <Route path="/register" element={<Register />} /> */}
          {/* <Route path="/checkout" element={<Checkout />} /> */}
          <Route path="/signup" element={<Signup />} />
          <Route path="/activate" element={<AccountActivation />} />
          <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
          <Route path="/termos-de-uso" element={<TermsOfUse />} />
          <Route
            path="/testar/agente/:agentName/:token"
            element={<PublicPlayground />}
          />
          <Route
            path="/conectar/whatsapp/:token"
            element={<WhatsAppConnect />}
          />
          {/* Redirect outras rotas para login se não logado */}
          <Route path="*" element={<Login />} />
        </Routes>
      )}

      {/* Footer */}
      {/* <Footer /> */}
    </>
  );
};

const Main = ({ isLoading, setIsLoading }) => {
  const { token: _token } = parseCookies();
  const [logged, setLogged] = useState(false);
  const userName = useRef();

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isPublicRoute =
      ["/login", "/register", "/activate", "/checkout", "/signup", "/politica-de-privacidade", "/termos-de-uso"].includes(currentPath) ||
      currentPath.startsWith("/testar") ||
      currentPath.startsWith("/conectar");

    if (isPublicRoute) {
      setLogged(false);
      setIsLoading(false);
    } else {
      setLogged(!!_token);
      if (!_token) {
        setIsLoading(false);
      }
    }
  }, [_token, setIsLoading]);

  return (
    <WhitelabelProvider>
      <Router>
        <UserProvider userName={userName} setIsLoading={setIsLoading}>
          <NotificationsProvider>
            <AppLayout
              isLoading={isLoading}
              logged={logged}
              setIsLoading={setIsLoading}
            />
          </NotificationsProvider>
        </UserProvider>
      </Router>
    </WhitelabelProvider>
  );
};

export default Main;
